import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';
import { HowTo, WithContext } from 'schema-dts';

import { CHANNEL, stepByStepPageSlugs } from '@boss/constants/b2b-b2c';
import { getPaths } from '@boss/contentful-client';
import { getBlogPost } from '@boss/contentful-client/b2b-b2c';
import { Environment, LOCALE_CODE, StepByStepPlanFields } from '@boss/types/b2b-b2c';
import { defaultLocale } from 'config/locales.config';

import { SEO } from '../../components';
import { PageTypes } from '../../constants';
import { RoadmapDetailPage } from '../../containers';
import Layout from '../../layout/default';
import getServerSideTranslations from '../../utils/getServerSideTranslations';
import { mapContentfulToNextLocale, mapNextToContentfulLocaleMarket } from '../../utils/localeMapper';
import { getMappedNavigation } from '../../utils/navigation';

const CONTENT_TYPE = 'stepByStepPlan';

export const config = {
  maxDuration: 30,
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  return await getPaths({
    preview: false,
    channel: CHANNEL,
    contentType: CONTENT_TYPE,
    localeMapper: mapContentfulToNextLocale,
    locales: locales as string[],
  });
};

export const getStaticProps = async ({
  preview,
  params,
  locale,
}: GetStaticPropsContext<{ slug: string; locale: LOCALE_CODE }>) => {
  if (!locale || locale === 'default') {
    return {
      notFound: true,
    };
  }
  const contentfulLocaleMarket = mapNextToContentfulLocaleMarket(locale);
  const slug = params?.slug;

  if (!slug || slug === 'home') {
    return {
      notFound: true,
    };
  }

  const [blogPost, navigation] = await Promise.all([
    getBlogPost<StepByStepPlanFields>({
      preview: !!preview,
      locale: contentfulLocaleMarket as LOCALE_CODE,
      slug,
      channel: CHANNEL,
      defaultLocale: defaultLocale as LOCALE_CODE,
    }),
    getMappedNavigation(contentfulLocaleMarket, !!preview),
  ]);

  if (!slug || slug === 'home' || !blogPost) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageType: PageTypes.ROADMAP_DETAIL,
      preview: !!preview,
      locale,
      blogPost,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      navigation,
      ...(await getServerSideTranslations(locale, ['common', 'stores', 'routes', 'color', 'forms'])),
    },
  };
};

const Index = ({
  blogPost,
  locale,
  environment,
  preview,
  navigation,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation();
  const pageTitle = blogPost.stepsTitle;
  const pageDescription = blogPost.description ? documentToPlainTextString(blogPost.description.content) : pageTitle;
  const crumbles = [
    { href: stepByStepPageSlugs[locale], label: t('roadmapLabel', { ns: 'common' }) },
    { href: blogPost.slug, label: pageTitle },
  ];
  const localizedSlugs = blogPost.translatedSlugs;

  const structuredData: WithContext<HowTo>[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      image: `https:${blogPost.mainImage?.image?.file?.url}`,
      video: {
        '@type': 'VideoObject',
        contentUrl: blogPost.video?.externalVideoUrl,
        thumbnailUrl: `https:${blogPost.video?.thumbnail?.image?.file?.url}`,
        name: blogPost.video?.alternativeText,
      },
      name: blogPost.stepsTitle,
      description: blogPost.description?.content ? documentToPlainTextString(blogPost.description?.content) : '',
      estimatedCost: blogPost.budget,
      totalTime: blogPost.duration,
      educationalLevel: blogPost.difficulty,
      steps: blogPost.steps.map((step, i) => ({
        '@type': 'HowToStep',
        position: i,
        name: step.stepTitle,
        text: step?.content ? documentToPlainTextString(step.content?.content) : '',
      })),
    },
  ];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={pageDescription}
      imageSrc={blogPost.mainImage?.image?.file?.url}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      structuredData={structuredData}
      title={pageTitle}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        <RoadmapDetailPage blogPost={blogPost} />
      </Layout>
    </SEO>
  );
};

export default Index;
